import { EnvironmentProviders, inject, Injectable, Provider } from '@angular/core';
import {
  IFloatingPopupActions,
  IFloatingPopupConfig,
  IFloatingPopupGroupInstance,
} from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { IFloatingPopupEntityService } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-entity-service';
import { FpLoggerNoiseContentComponent } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/components/content/fp-logger-noise-content.component';
import { FpLoggerNoiseMinimizedContentComponent } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/components/minimized-content/fp-logger-noise-minimized-content.component';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { FpLoggerNoiseTabsLocalize } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/localize/fp-logger-noise-tabs';
import { _, TranslateService } from '@ngx-translate/core';
import { fpLoggerNoiseProvider } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/providers/fp-logger-noise.providers';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { of } from 'rxjs';
import { formatSerialNo } from '@app/shared/utils/string';
import { ITranslationSubscription } from '@app/core/interfaces/localization';

@Injectable({ providedIn: 'root' })
export class FpLoggerNoiseService implements IFloatingPopupEntityService<ILdLogger> {
  protected popupService = inject(FloatingPopupService);
  private translateService = inject(TranslateService);

  // todo: can be dynamic according to the project properties for example if project have AiPredictor then FloatingPopupLoggerNoiseTabs.LeakScore, need to hide
  private floatingPopupGroup: IFloatingPopupGroupInstance = {
    id: 'logger-noise-popups',
    orderWeight: 2,
    minimizeTitle: (count: number) =>
      this.translateService.instant(_('@@floating-popup-group-logger-noise-name'), { I18N_VAR_COUNT: count }),
    tabs: {
      values: [
        FpLoggerNoiseTabs.Histogram,
        FpLoggerNoiseTabs.SignalSpectrum,
        FpLoggerNoiseTabs.LeakScore,
        FpLoggerNoiseTabs.AIPredictor,
        FpLoggerNoiseTabs.MinNoise,
        FpLoggerNoiseTabs.Spread,
        FpLoggerNoiseTabs.Summary,
        FpLoggerNoiseTabs.Comments,
        FpLoggerNoiseTabs.Pictures,
      ],
      localize: FpLoggerNoiseTabsLocalize,
    },
  };

  private readonly instanceProviders: (Provider | EnvironmentProviders)[] = [fpLoggerNoiseProvider()];

  public open(
    item: ILdLogger,
    actions?: IFloatingPopupActions,
    selectedTab: FpLoggerNoiseTabs = FpLoggerNoiseTabs.Histogram
  ) {
    const loggerSerialNo = item.deployment.serialNo;

    const title = this.translateService.get(_('@@floating-popup-header-logger-noise-name'), {
      I18N_VAR_LOGGER_SERIAL_NO: formatSerialNo(loggerSerialNo),
    });

    const shortTitle: ITranslationSubscription = of(`${formatSerialNo(loggerSerialNo)}`);

    const floatingPopupConfig: IFloatingPopupConfig = {
      component: FpLoggerNoiseContentComponent,
      minimizedComponent: FpLoggerNoiseMinimizedContentComponent,
      selectedTab,
      instanceProviders: this.instanceProviders,
      width: 840,
      height: 500,
      minWidth: 480,
      actions,
    };

    this.popupService.open<ILdLogger>({ ...floatingPopupConfig, title, shortTitle }, item, this.floatingPopupGroup);
  }
}
